import { stringify } from 'querystring'

import { exhaustiveSwitchError } from '@common/util/exhaustiveSwitchError'
import { ReportType, UnitGroup } from '~/graphql/generated/query.types'
import { LeadsViewName } from '~/model/leadsViews'
import { SalesFunnelViewName } from '~/model/salesFunnelViews'

export type WebRoute = {
  pathname: string
  query: { view?: string } & Record<string, string | undefined>
}

export enum OverviewRoute {
  dashboard = 'dashboard',
  metrics = 'metrics',
  insights = 'insights',
}

export function generateOverviewRoute(
  route?: OverviewRoute,
  options?: { portfolioSid?: string }
): WebRoute {
  return {
    pathname: 'overview',
    query: { ...options, view: route },
  }
}

export enum PropertiesRoute {
  summary = 'summary',
  pricing = 'pricing',
  pricingHistory = 'pricingHistory',
  units = 'units',
  leases = 'leases',
  comps = 'comps',
  salesFunnel = 'salesFunnel',
  specials = 'specials',
  amenities = 'amenities',
  leads = 'leads',
  settings = 'settings',
}

export enum CompPropertiesViewName {
  Overview = 'overview',
  Table = 'table',
  Detail = 'detail',
  Manage = 'manage',
}

export function generatePropertiesRoute(
  route?: PropertiesRoute,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute,
  options?: { portfolioSid?: string; propertySid?: string; group?: UnitGroup }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute,
  options?: { portfolioSid?: string; propertySid?: string; group?: UnitGroup }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.units,
  options?: { portfolioSid?: string; propertySid?: string; unitSid?: string }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.leases,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.comps,
  options?: { portfolioSid?: string; propertySid?: string; compsView?: CompPropertiesViewName }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.specials,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.amenities,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.settings,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.leads,
  options?: {
    portfolioSid?: string
    propertySid?: string
    guestcardSid?: string
    leadsView?: LeadsViewName
    from?: string
    to?: string
  }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute.salesFunnel,
  options?: {
    portfolioSid?: string
    propertySid?: string
    guestcardSid?: string
    leadsView?: LeadsViewName
    salesFunnelView?: SalesFunnelViewName
    from?: string
    to?: string
  }
): WebRoute
export function generatePropertiesRoute(
  route?: PropertiesRoute,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute {
  return {
    pathname: 'properties',
    query: { ...options, view: route },
  }
}

export enum PropertySettingsRoute {
  basic = 'basic',
  leaseUp = 'leaseUp',
  autoPilot = 'autoPilot',
  price = 'price',
  rentMatrix = 'rentMatrix',
  leaseExpirationTargets = 'leaseExpirationTargets',
  rentControl = 'rentControl',
  renewals = 'renewals',
  notifications = 'notifications',
  rentTargets = 'rentTargets',
  customUnitGroupings = 'customUnitGroupings',
  unitTypes = 'unitTypes',
}

export function generatePropertySettingsRoute(
  route?: PropertySettingsRoute,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute {
  return {
    pathname: 'properties',
    query: { ...options, view: PropertiesRoute.settings, settingsView: route },
  }
}

export enum RenewalsRoute {
  expirations = 'expirations',
  monthToMonth = 'monthToMonth',
  batches = 'batches',
  summary = 'summary',
}

export function generateRenewalsRoute(
  route?: RenewalsRoute.summary,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generateRenewalsRoute(
  route?: RenewalsRoute.expirations,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generateRenewalsRoute(
  route?: RenewalsRoute.monthToMonth,
  options?: { portfolioSid?: string; propertySid?: string }
): WebRoute
export function generateRenewalsRoute(
  route?: RenewalsRoute.batches,
  options?: {
    portfolioSid?: string
    propertySid?: string
    renewalRequestSid?: string
    activeTab?: string
  }
): WebRoute
export function generateRenewalsRoute(
  route?: RenewalsRoute,
  options?:
    | { portfolioSid?: string; propertySid?: string }
    | { portfolioSid?: string; propertySid?: string; renewalRequestSid?: string }
): WebRoute {
  return {
    pathname: 'renewals',
    query: { ...options, view: route },
  }
}

export enum ReportsRoute {
  metrics = 'metrics',
  reports = 'reports',
  scheduled = 'scheduled',
  history = 'history',
  preview = 'preview',
}

export function generateReportsRoute(route?: ReportsRoute.metrics, options?: {}): WebRoute
export function generateReportsRoute(
  route?: ReportsRoute.reports,
  options?: { reportType?: undefined; reportSid?: string; sid?: string }
): WebRoute
export function generateReportsRoute(
  route?: ReportsRoute.history,
  options?: { reportType?: undefined; reportSid?: string; sid?: string }
): WebRoute
export function generateReportsRoute(
  route?: ReportsRoute.scheduled,
  options?: { reportType?: undefined; reportSid?: string; sid?: string }
): WebRoute
export function generateReportsRoute(
  route?: ReportsRoute.preview,
  options?: { reportType: ReportType; reportSid?: string; sid?: string }
): WebRoute
export function generateReportsRoute(
  route?: ReportsRoute,
  options?: { reportType?: ReportType; reportSid?: string; sid?: string }
): WebRoute {
  return {
    pathname: 'reports',
    query: { ...options, view: route },
  }
}

export enum WatchlistRoute {
  finder = 'finder',
  comps = 'comps',
}

export function generateWatchlistRoute(route?: WatchlistRoute.finder, options?: {}): WebRoute
export function generateWatchlistRoute(
  route?: WatchlistRoute.comps,
  options?: {
    prospectivePropertySid?: string
    compsView?: CompPropertiesViewName
    showAddComps?: boolean
  }
): WebRoute
export function generateWatchlistRoute(
  route?: WatchlistRoute,
  options?: {
    prospectivePropertySid?: string
    compsView?: CompPropertiesViewName
    showAddComps?: boolean
  }
): WebRoute {
  return {
    pathname: 'watchlist',
    query: { ...options, view: route, showAddComps: options?.showAddComps ? 'true' : undefined },
  }
}

export enum CompanyRoute {
  information = 'information',
  properties = 'properties',
  portfolios = 'portfolios',
  ownershipGroups = 'ownership_groups',
  users = 'users',
  pricing = 'pricing',
  recipientGroup = 'recipient_group',
  notifications = 'notifications',
}
export function generateCompanyRoute(route?: CompanyRoute): WebRoute {
  return {
    pathname: 'company',
    query: { view: route },
  }
}

export enum AdminRoute {
  users = 'users',
  featureFlags = 'featureFlags',
  jobs = 'jobs',
  jobQueues = 'jobQueues',
  alerts = 'alerts',
  comps = 'comps',
  firms = 'firms',
  properties = 'properties',
  metrics = 'metrics',
  models = 'models',
  recommendations = 'recommendations',
  aiCrawls = 'aiCrawls',
}
export function generateAdminRoute(
  route?: AdminRoute,
  options: {
    firm?: string
  } = {}
): WebRoute {
  return {
    pathname: 'admin',
    query: { ...options, view: route },
  }
}

export function makeURLStringFromURLObject(
  urlObject: WebRoute,
  options?: { hash?: string }
): string {
  // Filter out properties with null or undefined values
  const filteredQuery = Object.entries(urlObject.query)
    .filter(([_key, value]) => value !== null && value !== undefined)
    .reduce(
      (obj, [key, value]) => {
        obj[key] = value as any
        return obj
      },
      {} as Record<string, string>
    )

  // Stringify the result
  const query = stringify(filteredQuery)
  return urlObject.pathname + (query ? '?' + query : '') + (options?.hash ? '#' + options.hash : '')
}

export function getRouteLabel(
  route:
    | OverviewRoute
    | PropertiesRoute
    | RenewalsRoute
    | ReportsRoute
    | CompanyRoute
    | AdminRoute
    | WatchlistRoute
): string {
  switch (route) {
    case OverviewRoute.dashboard:
      return 'Dashboard'
    case OverviewRoute.metrics:
      return 'Metrics'
    case OverviewRoute.insights:
      return 'Insights'

    case PropertiesRoute.summary:
      return 'Summary'
    case PropertiesRoute.pricing:
      return 'Pricing'
    case PropertiesRoute.pricingHistory:
      return 'Pricing history'
    case PropertiesRoute.units:
      return 'Units'
    case PropertiesRoute.leases:
      return 'Leases'
    case PropertiesRoute.comps:
      return 'Comps'
    case PropertiesRoute.salesFunnel:
      return 'Sales funnel'
    case PropertiesRoute.specials:
      return 'Specials'
    case PropertiesRoute.amenities:
      return 'Amenities'
    case PropertiesRoute.leads:
      return 'Leads'
    case PropertiesRoute.settings:
      return 'Settings'

    case RenewalsRoute.summary:
      return 'Summary'
    case RenewalsRoute.expirations:
      return 'Lease expirations'
    case RenewalsRoute.monthToMonth:
      return 'Month-to-month'
    case RenewalsRoute.batches:
      return 'Batches'

    case ReportsRoute.metrics:
      return 'Metrics'
    case ReportsRoute.reports:
      return 'Reports'
    case ReportsRoute.scheduled:
      return 'Schedules'
    case ReportsRoute.history:
      return 'History'
    case ReportsRoute.preview:
      return 'Preview'

    case CompanyRoute.information:
      return 'Information'
    case CompanyRoute.properties:
      return 'Properties'
    case CompanyRoute.portfolios:
      return 'Portfolios'
    case CompanyRoute.ownershipGroups:
      return 'Ownership groups'
    case CompanyRoute.users:
      return 'Users'
    case CompanyRoute.pricing:
      return 'Pricing'
    case CompanyRoute.recipientGroup:
      return 'Recipient groups'
    case CompanyRoute.notifications:
      return 'Notifications'

    case AdminRoute.users:
      return 'Users'
    case AdminRoute.featureFlags:
      return 'Feature flags'
    case AdminRoute.jobs:
      return 'Jobs'
    case AdminRoute.jobQueues:
      return 'Job queues'
    case AdminRoute.alerts:
      return 'Alerts'
    case AdminRoute.comps:
      return 'Comps'
    case AdminRoute.firms:
      return 'Firms'
    case AdminRoute.properties:
      return 'Properties'
    case AdminRoute.metrics:
      return 'Metrics'
    case AdminRoute.models:
      return 'Models'
    case AdminRoute.recommendations:
      return 'Recommendations'
    case AdminRoute.aiCrawls:
      return 'AI crawls'

    case WatchlistRoute.finder:
      return 'Prospects'
    case WatchlistRoute.comps:
      return 'Comps'
    default:
      throw exhaustiveSwitchError(route)
  }
}

export function getPropertySettingsRouteLabel(route: PropertySettingsRoute): string {
  switch (route) {
    case PropertySettingsRoute.basic:
      return 'Basics'
    case PropertySettingsRoute.leaseUp:
      return 'Lease up'
    case PropertySettingsRoute.autoPilot:
      return 'Autopilot'
    case PropertySettingsRoute.price:
      return 'Pricing'
    case PropertySettingsRoute.rentMatrix:
      return 'Rent matrix'
    case PropertySettingsRoute.leaseExpirationTargets:
      return 'Lease expiration targets'
    case PropertySettingsRoute.rentControl:
      return 'Rent control'
    case PropertySettingsRoute.renewals:
      return 'Renewals'
    case PropertySettingsRoute.notifications:
      return 'Notifications'
    case PropertySettingsRoute.rentTargets:
      return 'Rent targets'
    case PropertySettingsRoute.customUnitGroupings:
      return 'Custom unit groups'
    case PropertySettingsRoute.unitTypes:
      return 'Unit types'
    default:
      throw exhaustiveSwitchError(route)
  }
}
